import { useCan } from '@features/auth';
import { ReadOnlyNotice, setByText, useTenantFlags } from '@features/feature-flags';
import {
    Badge,
    Button,
    Card,
    CardHeader,
    EmptyState,
    ErrorState,
    FlagKeyPill,
    LoadingRows,
    Modal,
    PropagationNotice,
    WriteError,
} from '@shared/ui';
import { useMemo, useState } from 'react';

import type { TenantFlagView } from '@features/feature-flags';

import {
    useActivateComponent,
    useComponentSettings,
    useDeactivateComponent,
    useComponents,
} from '../hooks/use-components';

import { componentCard } from './component-card';
import {
    componentCardCopy,
    createdByLabel,
    deactivationConfirmCopy,
    memberShortfallNotice,
    seedIncompleteCaveat,
    survivingGrantsNotice,
} from './component-card-copy';
import {
    componentCardAction,
    componentCardTone,
    MEMBER_SOURCE_LABEL,
    memberStateChip,
    settingKindLabel,
    settingStatusChip,
} from './component-card-presentation';
import { explainComponentWriteFailure } from './component-error-copy';
import { sortComponents } from './component-list';

import type { ComponentMemberFlag } from './component-card';
import type { Component } from '../schemas/component';

/**
 * What this one museum has, component by component — the client-side inverse of
 * `CatalogComponentsTab`.
 *
 * ## Why it reads the catalog *and* the museum's flags
 *
 * Neither answers the question alone. The catalog says what exists to be sold;
 * whether this museum holds one of them is a fact about that museum's override
 * rows and the grantors on them, which is what `useTenantFlags` returns. The
 * join is `component-card.ts`'s, not this file's, for the rule this workspace
 * runs on: vitest is on `node` with no jsdom, and a state derived inside a
 * render is a state no test can read.
 *
 * One flags read serves every card. It could have been one read per card — the
 * settings query below is exactly that — but the flags route answers the whole
 * catalog for a museum in one call, and N copies of the same answer would be N
 * chances for two cards on one screen to disagree about what the museum holds.
 *
 * ## Why cards and not a table
 *
 * `CatalogComponentsTab` is a table because it is scanned: one line per row,
 * compared down a column. This screen is read one component at a time — a
 * state, a reason, a checklist and a button — and a checklist does not fit in a
 * cell without becoming something an operator has to expand to see.
 */
export function TenantComponentsPanel({ tenantId }: { tenantId: string }) {
    const components = useComponents();
    const flags = useTenantFlags(tenantId);

    // Must match `@RequiresRole('ops-csm')` on `PUT`/`DELETE
    // /api/components/:id/activations/:tenantId`. Deliberately *not* the
    // `engineer` gate `CatalogComponentsTab` uses: that one guards *defining* a
    // component, where an id is permanent and its members decide what a museum
    // can be billed for. Selling an existing one to a museum is the CSM's job.
    const canActivate = useCan('ops-csm');

    // A `?? []` inline would mint a new array every render and defeat the memo.
    const rows = components.data ?? NO_ROWS;
    const sorted = useMemo(() => sortComponents(rows), [rows]);

    // Both reads or neither: a card needs the catalog row *and* the museum's
    // grants to say anything at all, so half the data renders every card in a
    // state that is not this museum's.
    if (components.isPending || flags.isPending) {
        return <LoadingRows label="Loading this client's components…" />;
    }

    if (components.isError || flags.isError) {
        // Whichever failed. Retrying both is harmless — a query that already has
        // its answer refetches it — and it saves an operator working out which
        // of two reads they are being told about.
        const error = components.error ?? flags.error;

        return (
            <ErrorState
                error={error ?? new Error('Could not load this tab')}
                onRetry={() => {
                    void components.refetch();
                    void flags.refetch();
                }}
            />
        );
    }

    return (
        <>
            <ReadOnlyNotice />
            <PropagationNotice />

            <Card className="mt-4">
                <CardHeader
                    title="Components for this client"
                    subtitle="Each one is a bundle of entitlement flags. Activating grants every flag in it."
                />

                {sorted.length === 0 ? (
                    <EmptyState
                        title="There are no components yet"
                        description="Nothing has been defined in the catalog, so there is nothing to activate for this client."
                    />
                ) : (
                    <div className="flex flex-col gap-4 p-5">
                        {sorted.map((component) => (
                            <ComponentCardPanel
                                key={component.id}
                                component={component}
                                flags={flags.data}
                                tenantId={tenantId}
                                canActivate={canActivate}
                            />
                        ))}
                    </div>
                )}
            </Card>
        </>
    );
}

/** Hoisted so `useMemo` above has a stable empty array to depend on. */
const NO_ROWS: readonly Component[] = [];

/**
 * One component, as this museum has it.
 *
 * The settings query is per card because that is the route's shape, and it is
 * the right trade: each card renders as soon as its own read lands, and a
 * component whose checklist fails to load does not blank the other five. Its
 * absence is passed through to `componentCard` as `undefined` rather than as an
 * empty list — "no rows known" and "no rows exist" are different facts, and only
 * the second one means this museum was never sold the component.
 */
function ComponentCardPanel({
    component,
    flags,
    tenantId,
    canActivate,
}: {
    component: Component;
    flags: readonly TenantFlagView[];
    tenantId: string;
    canActivate: boolean;
}) {
    const settings = useComponentSettings(tenantId, component.id);
    const card = componentCard({ component, flags, settings: settings.data });
    const copy = componentCardCopy({
        state: card.state,
        outstanding: card.outstanding,
        displayName: component.displayName,
    });
    const action = componentCardAction(card.state);

    const activate = useActivateComponent(tenantId);
    const deactivate = useDeactivateComponent(tenantId);
    const busy = activate.isPending || deactivate.isPending;
    const error = activate.error ?? deactivate.error;

    /**
     * Held per card rather than in the panel, for the reason `TenantFlagsPanel`
     * gives for holding its dialogs per row: `Modal` is a native `<dialog>`
     * rendered in place, and `showModal()` lifts it into the top layer so the
     * surrounding layout cannot reach it. A single dialog hoisted to the panel
     * would have to carry which card opened it, which is state that can
     * disagree with the mutation it confirms.
     */
    const [confirming, setConfirming] = useState(false);

    /**
     * The 200 that is not a clean success, kept until the operator has read it.
     *
     * Not derived from `activate.data`: a re-render that resets the mutation
     * would take the caveat away with it, and this is the one outcome nothing
     * else on the card shows — the card behind it now reads "Active", correctly,
     * and says nothing about the checklist rows that were never created.
     */
    const [caveat, setCaveat] = useState<string | null>(null);

    const write = () => {
        switch (action) {
            case 'activate':
                setCaveat(null);
                activate.mutate(
                    { componentId: component.id },
                    {
                        onSuccess: (result) => {
                            if (result.ok === 'seed-incomplete') setCaveat(result.message);
                        },
                    },
                );
                return;
            case 'deactivate':
                // Opens the dialog instead of writing. Withdrawing an
                // entitlement is not symmetric with granting one: the museum
                // loses a capability it is paying for, and the second warning in
                // `deactivationConfirmCopy` is a consequence no card can show.
                if (!confirming) {
                    setConfirming(true);
                    return;
                }
                deactivate.mutate(
                    { componentId: component.id },
                    {
                        onSuccess: () => {
                            setConfirming(false);
                        },
                    },
                );
                return;
            case 'none':
                return;
        }
    };

    const explained = error ? explainComponentWriteFailure(error) : null;

    return (
        <div className="rounded-lg border border-line bg-surface-sunken/40 p-4">
            <div className="flex flex-wrap items-start gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-[14px] font-semibold text-content-strong">
                            {component.displayName}
                        </h4>
                        <Badge tone={componentCardTone(card.state)} dot>
                            {copy.status}
                        </Badge>
                    </div>
                    <p className="mt-1 max-w-2xl text-[12.5px] leading-relaxed text-content-muted">
                        {copy.description}
                    </p>
                </div>

                {/*
                 * No button at all in the `not-activatable` state, rather than a
                 * disabled one: a disabled control says "not you, not now", and
                 * the truth is that nobody can ever activate this component for
                 * anybody. `copy.action` is undefined in exactly that state, and
                 * `componentCardAction` independently says `none`.
                 */}
                {action !== 'none' && copy.action !== undefined && (
                    <Button
                        variant={action === 'deactivate' ? 'ghost' : 'primary'}
                        disabled={!canActivate || busy}
                        onClick={write}
                    >
                        {busy ? 'Saving…' : copy.action}
                    </Button>
                )}
            </div>

            {/*
             * A caveat beside an active card, never an error. The museum IS
             * entitled — the API answered 200 for exactly this reason — and the
             * only thing short is the checklist.
             */}
            {caveat !== null && <SeedIncompleteNotice message={caveat} />}

            {/*
             * The two sentences the card used to be missing, and only ever one
             * of them at a time: a held component whose members do not all
             * resolve on (`D15` rule 2), or an unheld one whose members are
             * still on by somebody else's grant (`D5`). Both are derived in
             * `component-card-copy.ts` and return `undefined` when there is
             * nothing to say, so neither renders an empty box.
             */}
            <MemberNotice
                text={
                    action === 'deactivate'
                        ? memberShortfallNotice(card.members)
                        : survivingGrantsNotice(card.members)
                }
            />

            <MemberList members={card.members} />

            <SettingsChecklist card={card} settingsFailed={settings.isError} />

            <p className="mt-3 text-[11.5px] text-content-faint">
                {createdByLabel(component.createdBy)}
            </p>

            {/*
             * Only while the dialog is closed. An error belongs next to the
             * button that caused it, and repeating it behind an open dialog that
             * is already showing it reads as two separate failures.
             */}
            {error && !confirming && (
                <div className="mt-3">
                    {explained ? (
                        <ComponentWriteNotice copy={explained} />
                    ) : (
                        <WriteError error={error} />
                    )}
                </div>
            )}

            <DeactivateConfirmDialog
                displayName={component.displayName}
                open={confirming}
                isPending={deactivate.isPending}
                error={confirming ? deactivate.error : null}
                onCancel={() => {
                    deactivate.reset();
                    setConfirming(false);
                }}
                onConfirm={write}
            />
        </div>
    );
}

/** The 200 that granted the entitlement and not the whole checklist. */
function SeedIncompleteNotice({ message }: { message: string }) {
    const copy = seedIncompleteCaveat(message);

    return (
        <div
            role="status"
            className="mt-3 rounded-lg border border-warning-line bg-warning-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-warning-strong"
        >
            <p className="font-semibold">{copy.title}</p>
            {message.length > 0 && <p className="mt-0.5 text-warning-text">{message}</p>}
            <p className="mt-0.5 text-warning-text">{copy.detail}</p>
        </div>
    );
}

/**
 * A component write whose *code* means something its status does not.
 *
 * Rendered in `WriteError`'s shape rather than in its place: the shared panel
 * still handles a 403, a version conflict and a validation list, and
 * `explainComponentWriteFailure` returns `null` for all of those so this branch
 * never sees them.
 */
function ComponentWriteNotice({
    copy,
}: {
    copy: NonNullable<ReturnType<typeof explainComponentWriteFailure>>;
}) {
    return (
        <div
            role="alert"
            className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-warning-strong"
        >
            <p className="font-semibold">{copy.title}</p>
            {copy.body.length > 0 && <p className="mt-0.5 text-warning-text">{copy.body}</p>}
            {copy.detail !== undefined && <p className="mt-1 text-warning-text">{copy.detail}</p>}
        </div>
    );
}

/** One sentence about the members, or nothing at all. */
function MemberNotice({ text }: { text: string | undefined }) {
    if (text === undefined) return null;

    return (
        <p
            role="status"
            className="mt-3 rounded-lg border border-warning-line bg-warning-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-warning-strong"
        >
            {text}
        </p>
    );
}

/**
 * What each flag in this component actually resolves to for this museum
 * (`D15` rules 2 and 4).
 *
 * The verdict, never the stored toggle: an override of `true` on an archived
 * flag still resolves `false`, and showing the toggle would tell an operator
 * the opposite of the truth — the rule `tenant-flag.ts:17-19` states for the
 * flags list and this card now follows.
 *
 * What holds each row up is named with the flags list's own `setByText`, so an
 * operator who reads "This client" here finds the same two words in the **Set
 * by** column rather than a synonym.
 */
function MemberList({ members }: { members: readonly ComponentMemberFlag[] }) {
    if (members.length === 0) return null;

    return (
        <ul className="mt-3 flex flex-col gap-1.5 border-t border-line-subtle pt-3">
            {members.map((member) => {
                const chip = memberStateChip(member);

                return (
                    <li
                        key={member.key}
                        className="flex flex-wrap items-center gap-2 text-[12.5px] text-content-body"
                    >
                        <FlagKeyPill>{member.key}</FlagKeyPill>
                        <Badge tone={chip.tone}>{chip.label}</Badge>
                        {member.known && (
                            <span className="text-content-faint">
                                {setByText(member.provenance)} · from{' '}
                                {MEMBER_SOURCE_LABEL[member.source]}
                            </span>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * How far this museum's configuration got.
 *
 * Rendered for every card that has rows, including the off-and-provisioned one
 * — that is the state the list is most useful in, because it is the evidence
 * that re-activating does not start over.
 *
 * A failed settings read is said out loud rather than rendered as an empty
 * checklist. An empty list means "this museum has no settings", and showing
 * that for a read that never landed would tell an operator there is no work
 * outstanding on a component that may be full of it.
 */
function SettingsChecklist({
    card,
    settingsFailed,
}: {
    card: ReturnType<typeof componentCard>;
    settingsFailed: boolean;
}) {
    if (settingsFailed) {
        return (
            <p className="mt-3 text-[12px] text-content-faint">
                The configuration checklist for this component could not be loaded. The state above
                is read from this client&rsquo;s flags and is unaffected.
            </p>
        );
    }

    if (card.settings.length === 0) return null;

    return (
        <ul className="mt-3 flex flex-col gap-1.5 border-t border-line-subtle pt-3">
            {card.settings.map((row) => {
                const chip = settingStatusChip(row);

                return (
                    <li
                        key={row.id}
                        className="flex flex-wrap items-center gap-2 text-[12.5px] text-content-body"
                    >
                        <span className="font-medium text-content-strong">{row.label}</span>
                        <span className="text-content-faint">{settingKindLabel(row.kind)}</span>
                        {/*
                         * A status with no copy in this build is rendered as the
                         * value itself, in the monospace pill every raw key in
                         * this app uses — so it reads as data the backend sent
                         * rather than as a word this screen chose.
                         */}
                        {chip.verbatim ? (
                            <FlagKeyPill>{chip.label}</FlagKeyPill>
                        ) : (
                            <Badge tone={chip.tone}>{chip.label}</Badge>
                        )}
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * The stop before an entitlement is withdrawn.
 *
 * `TenantOverrideConfirmDialog`'s shape exactly, including the shared
 * `PropagationNotice` — the component, never a second wording of it. The
 * warning beside it is the half `PropagationNotice` cannot say: the delay is
 * about when the change *arrives*, and a dispute cycle this client was already
 * admitted to is not reached by it at all. An operator who deactivates to stop
 * a cycle and is not told this will watch it continue and conclude the
 * deactivation failed.
 */
function DeactivateConfirmDialog({
    displayName,
    open,
    isPending,
    error,
    onCancel,
    onConfirm,
}: {
    displayName: string;
    open: boolean;
    isPending: boolean;
    error: Error | null;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    const copy = deactivationConfirmCopy(displayName);

    return (
        <Modal
            open={open}
            onClose={() => {
                if (!isPending) onCancel();
            }}
            dismissable={!isPending}
            title={copy.title}
            description={copy.description}
            footer={
                <>
                    <Button variant="ghost" onClick={onCancel} disabled={isPending}>
                        Cancel
                    </Button>
                    <Button variant="danger" disabled={isPending} onClick={onConfirm}>
                        {isPending ? 'Saving…' : copy.confirmLabel}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-4">
                {error && <WriteError error={error} />}
                <p
                    role="note"
                    className="rounded-lg border border-warning-line bg-warning-surface px-3.5 py-2.5 text-[12.5px] leading-relaxed text-warning-strong"
                >
                    {copy.warning}
                </p>
                <PropagationNotice />
            </div>
        </Modal>
    );
}
